import { html } from '../lib/html.js';
import { flow } from '../lib/flow.js';
import { busy, toast, toastError, toastSuccess } from '../lib/ui.js';
import { me } from '../services/me.js';
import { session } from '../services/session.js';
import { workshops } from '../services/workshops.js';
import { Page, go } from './base.js';
import { space } from '../services/space.js';

const dateFmt = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' });

const CLOSED = {
  USED: ['Convite já usado', 'Este link já foi usado. Peça um novo convite ao responsável pela empresa.'],
  REVOKED: ['Convite cancelado', 'O responsável pela empresa cancelou este convite.'],
  EXPIRED: ['Convite expirado', 'Este link passou da validade. Peça um novo convite ao responsável pela empresa.'],
};

/**
 * /convite/:token — o mecânico abre o link recebido e aceita entrar na oficina.
 * Sem login ou sem cadastro completo, o token fica guardado (flow.invite) e o
 * usuário volta para cá sozinho depois de entrar/completar o cadastro.
 */
class InvitePage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar><ion-title>Convite</ion-title></ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" fullscreen>
        <div class="narrow body"></div>
      </ion-content>`;
  }

  setup() {
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (!el) return;
      if (el.dataset.action === 'accept') this.accept(el);
      if (el.dataset.action === 'home') go('/home', 'root');
      if (el.dataset.action === 'workshop') (space.set('business'), go('/workshop', 'root'));
    });
  }

  /** O ion-router passa :token como propriedade; o pathname é a garantia no primeiro carregamento. */
  get inviteToken() {
    if (this.token) return String(this.token);
    const m = location.pathname.match(/\/convite\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  async onEnter() {
    const token = this.inviteToken;
    const body = this.$('.body');
    body.innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';

    if (!session.isLoggedIn) {
      flow.invite = token;
      toast('Entre ou crie sua conta para aceitar o convite.', 'dark', 4000);
      return go('/login', 'root');
    }
    let profile;
    try {
      profile = await me.load();
    } catch (err) {
      return this.show('Não foi possível carregar', err.message);
    }
    if (!profile.profileComplete) {
      flow.invite = token;
      toast('Complete seu cadastro para aceitar o convite.', 'dark', 4000);
      return go('/complete-profile', 'root');
    }
    flow.invite = ''; // já chegou aqui: não redireciona de novo

    let inv;
    try {
      inv = await workshops.getInvitation(token);
    } catch (err) {
      if (err.status === 404) return this.show('Convite não encontrado', 'Confira se o link foi copiado inteiro.');
      return this.show('Não foi possível carregar', err.message);
    }
    this.inv = inv;

    if (CLOSED[inv.status]) return this.show(...CLOSED[inv.status]);
    if (inv.workshop.status !== 'APPROVED') return this.show('Empresa indisponível', `${inv.workshop.name} não está ativa no momento.`);
    const mine = profile.workshop;
    if (mine) return this.show('Você já faz parte de uma empresa', `Você está na equipe de ${mine.name}. Cada profissional participa de uma empresa por vez.`, 'workshop');

    const place = [inv.workshop.city, inv.workshop.state].filter(Boolean).join('/');
    body.innerHTML = html`
      <div class="intro">
        <ion-icon name="storefront-outline" class="empty-icon"></ion-icon>
        <h2>${inv.workshop.name}</h2>
        ${place ? html`<p class="hint">${place}</p>` : ''}
      </div>
      <ion-card>
        <ion-card-content>
          <p>Você foi convidado para fazer parte da equipe desta empresa como <strong>profissional</strong>.</p>
          <p class="hint">Ao aceitar, você passa a ver a área da empresa no app. Convite válido até ${dateFmt.format(new Date(inv.expiresAt))}.</p>
        </ion-card-content>
      </ion-card>
      <ion-button expand="block" data-action="accept" class="submit">Aceitar convite</ion-button>
      <ion-button expand="block" fill="clear" data-action="home">Agora não</ion-button>`.value;
  }

  show(title, message, target = 'home') {
    this.$('.body').innerHTML = html`
      <div class="center">
        <ion-icon name="alert-circle-outline" class="empty-icon"></ion-icon>
        <h2>${title}</h2>
        <p class="hint">${message}</p>
        <ion-button fill="outline" data-action="${target}">${target === 'workshop' ? 'Ver minha empresa' : 'Ir para o início'}</ion-button>
      </div>`.value;
  }

  async accept(button) {
    await busy(button, async () => {
      try {
        const r = await workshops.acceptInvitation(this.inviteToken);
        await me.load({ force: true }).catch(() => {});
        toastSuccess(`Bem-vindo à equipe de ${r.workshop.name}!`);
        (space.set('business'), go('/workshop', 'root'));
      } catch (err) {
        if (err.status === 410 || err.status === 409) return this.show('Não foi possível aceitar', err.message);
        toastError(err.message);
      }
    });
  }
}

customElements.define('page-invite', InvitePage);
